import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Save, ArrowLeft, Workflow, Loader2, Plus, Trash2, ChevronDown, ChevronUp, ShieldCheck, Users } from 'lucide-react';

const ROLES = ['Procurement', 'PM', 'Site Engineer', 'Finance', 'HR', 'Manager', 'MD'];

// Legacy role strings the PO-approve guard matches against (user.role).
const MATRIX_ROLE_OPTIONS = ['Procurement', 'PM', 'Site Engineer', 'Finance', 'HR', 'Manager', 'Director', 'MD', 'admin', 'super_admin'];

const fmtINR = (n) => (n == null ? '' : Number(n).toLocaleString('en-IN'));

// Compact checkbox dropdown (native <details> = no click-outside wiring).
function MultiPicker({ options, selected, onChange, placeholder, testid }) {
  const sel = selected || [];
  const toggle = (val) => onChange(sel.includes(val) ? sel.filter((x) => x !== val) : [...sel, val]);
  const selLabels = options.filter((o) => sel.includes(o.value)).map((o) => o.label);
  const label = selLabels.length ? selLabels.join(', ') : (placeholder || 'Select…');
  return (
    <details className="relative group" data-testid={testid}>
      <summary className="list-none cursor-pointer border border-slate-300 rounded-sm px-2 py-1.5 text-sm bg-white min-w-[150px] flex items-center justify-between gap-2">
        <span className="truncate text-slate-700">{label}</span>
        <ChevronDown size={12} className="text-slate-400 shrink-0" />
      </summary>
      <div className="absolute z-20 mt-1 max-h-56 overflow-auto bg-white border border-slate-200 rounded-sm shadow-lg p-1 min-w-[200px]">
        {options.length === 0 && <div className="text-xs text-slate-400 px-2 py-1">No options</div>}
        {options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 px-2 py-1 text-sm hover:bg-slate-50 cursor-pointer rounded-sm">
            <input type="checkbox" checked={sel.includes(o.value)} onChange={() => toggle(o.value)} />
            <span className="truncate">{o.label}</span>
          </label>
        ))}
      </div>
    </details>
  );
}

export default function ApprovalWorkflows() {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // --- Procurement Approval Matrix (DB-backed) ---
  const [matrix, setMatrix] = useState([]);
  const [matrixSaving, setMatrixSaving] = useState(false);
  const [users, setUsers] = useState([]);

  // --- Procurement Committee vendor-selection config ---
  const [committeeThreshold, setCommitteeThreshold] = useState(100000);
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [committeeSaving, setCommitteeSaving] = useState(false);

  useEffect(() => {
    apiGet('/settings/approval-workflows').then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    apiGet('/settings/approval-matrix').then((d) => {
      setMatrix(d?.tiers || []);
      if (d?.committee_threshold_amount != null) setCommitteeThreshold(d.committee_threshold_amount);
      if (Array.isArray(d?.committee_members)) setCommitteeMembers(d.committee_members);
    }).catch(() => {});
    apiGet('/user-management/users').then((d) => setUsers(Array.isArray(d) ? d : (d?.users || d?.items || []))).catch(() => {});
  }, []);

  const roleOptions = React.useMemo(() => {
    const set = new Set(MATRIX_ROLE_OPTIONS);
    matrix.forEach((t) => (t.allowed_roles || []).forEach((r) => set.add(r)));
    return [...set].map((r) => ({ value: r, label: r }));
  }, [matrix]);
  const memberOptions = React.useMemo(
    () => users.map((u) => ({ value: u.user_id || u.id, label: u.name || u.email || (u.user_id || u.id) })),
    [users],
  );

  // Committee members can be roles AND/OR specific users. Offer common exec roles
  // plus every user; any pre-saved value not in the list is preserved as-is.
  const COMMITTEE_ROLE_OPTIONS = ['CEO', 'CMD', 'President', 'Procurement', 'MD', 'Director', 'Finance'];
  const committeeOptions = React.useMemo(() => {
    const opts = [];
    const seen = new Set();
    COMMITTEE_ROLE_OPTIONS.forEach((r) => { opts.push({ value: r, label: `Role · ${r}` }); seen.add(r); });
    memberOptions.forEach((m) => { if (!seen.has(m.value)) { opts.push({ value: m.value, label: `User · ${m.label}` }); seen.add(m.value); } });
    committeeMembers.forEach((v) => { if (!seen.has(v)) { opts.push({ value: v, label: v }); seen.add(v); } });
    return opts;
  }, [memberOptions, committeeMembers]);

  const saveCommittee = async () => {
    if (Number(committeeThreshold) < 0) { toast.error('Threshold must be 0 or greater.'); return; }
    if (!committeeMembers.length) { toast.error('Add at least one committee member (role or user).'); return; }
    setCommitteeSaving(true);
    try {
      const res = await apiPut('/settings/committee-config', {
        committee_threshold_amount: Number(committeeThreshold),
        committee_members: committeeMembers,
      });
      setCommitteeThreshold(res.committee_threshold_amount);
      setCommitteeMembers(res.committee_members || []);
      toast.success('Committee config saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save committee config');
    }
    setCommitteeSaving(false);
  };

  const updateMatrixTier = (idx, field, value) => {
    setMatrix((m) => m.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };
  const addMatrixTier = () => {
    setMatrix((m) => {
      // new numeric tier inserted just before the unlimited top tier
      const insertAt = Math.max(m.length - 1, 0);
      const next = [...m];
      next.splice(insertAt, 0, { label: 'New Level', max_amount: 0, allowed_roles: ['MD'], allowed_member_ids: [] });
      return next;
    });
  };
  const removeMatrixTier = (idx) => setMatrix((m) => m.filter((_, i) => i !== idx));
  const moveMatrixTier = (idx, dir) => {
    setMatrix((m) => {
      const j = idx + dir;
      if (j < 0 || j >= m.length) return m;
      const next = [...m];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const saveMatrix = async () => {
    // Client-side guard: every non-top tier needs a positive max amount.
    const blank = matrix.slice(0, -1).findIndex((t) => t.max_amount === '' || t.max_amount == null || Number(t.max_amount) <= 0);
    if (blank !== -1) {
      toast.error(`Tier ${blank + 1} ("${matrix[blank].label || 'unnamed'}") needs a max amount greater than 0.`);
      return;
    }
    // Re-number level_order by position; the LAST tier is always the unlimited top tier.
    const tiers = matrix.map((t, i) => ({
      level_order: i + 1,
      label: (t.label || '').trim(),
      max_amount: i === matrix.length - 1 ? null : Number(t.max_amount),
      allowed_roles: t.allowed_roles || [],
      allowed_member_ids: t.allowed_member_ids || [],
    }));
    setMatrixSaving(true);
    try {
      const res = await apiPut('/settings/approval-matrix', { tiers });
      setMatrix(res.tiers || tiers);
      toast.success('Approval matrix saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save approval matrix');
    }
    setMatrixSaving(false);
  };

  const updateTier = (docType, idx, field, value) => {
    setData((d) => {
      const next = { ...d };
      next[docType] = [...next[docType]];
      next[docType][idx] = { ...next[docType][idx], [field]: field === 'max' ? (value === '' ? -1 : Number(value)) : value };
      return next;
    });
  };

  const addTier = (docType) => {
    setData((d) => ({ ...d, [docType]: [...d[docType], { max: -1, approver_role: 'MD' }] }));
  };

  const removeTier = (docType, idx) => {
    setData((d) => ({ ...d, [docType]: d[docType].filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiPut('/settings/approval-workflows', { workflows: data });
      toast.success('Approval workflows saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="p-6 md:p-8" data-testid="approval-workflows-page">
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#1F7A6E] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader overline="Settings · Approval Workflows" title="Approval Workflows" subtitle="Configure who approves what based on value tiers. Use −1 (or leave blank) for unlimited." icon={Workflow} />

      <div className="space-y-5 mt-6">
        {Object.entries(data).map(([docType, tiers]) => (
          <div key={docType} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`workflow-${docType}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-base">{docType} Approval Chain</div>
              <button onClick={() => addTier(docType)} className="text-xs flex items-center gap-1 px-2 py-1 border border-slate-300 rounded-sm hover:border-[#1F7A6E]" data-testid={`add-tier-${docType}`}>
                <Plus size={11} /> Add Tier
              </button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-display">
                  <th className="text-left py-2 pr-3 w-12">#</th>
                  <th className="text-left py-2 pr-3">{docType === 'Leave' ? 'Up to (days)' : 'Value Up To (₹)'}</th>
                  <th className="text-left py-2 pr-3">Approver Role</th>
                  <th className="w-16"></th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-3 text-slate-400">{i + 1}</td>
                    <td className="py-2 pr-3">
                      <input
                        type="number"
                        value={tier.max < 0 ? '' : tier.max}
                        onChange={(e) => updateTier(docType, i, 'max', e.target.value)}
                        placeholder="Unlimited"
                        className="w-40 border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <select
                        value={tier.approver_role}
                        onChange={(e) => updateTier(docType, i, 'approver_role', e.target.value)}
                        className="w-44 border border-slate-300 rounded-sm px-2 py-1.5 text-sm bg-white"
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td className="py-2">
                      {tiers.length > 1 && (
                        <button onClick={() => removeTier(docType, i)} className="text-rose-500 hover:text-rose-700"><Trash2 size={13} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* ---- Procurement Approval Matrix (DB-backed, replaces hardcoded tiers) ---- */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mt-6" data-testid="approval-matrix-card">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-[#1F7A6E]" />
            <div className="font-display font-bold text-base">Procurement Approval Matrix</div>
          </div>
          <button onClick={addMatrixTier} className="text-xs flex items-center gap-1 px-2 py-1 border border-slate-300 rounded-sm hover:border-[#1F7A6E]" data-testid="add-matrix-tier">
            <Plus size={11} /> Add Level
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Who can approve a Purchase Order by value. Levels are ordered low → high; the top level has no upper limit. A user may approve if their role is allowed OR they are an explicitly listed member.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-display">
                <th className="text-left py-2 pr-2 w-16">Order</th>
                <th className="text-left py-2 pr-3">Label</th>
                <th className="text-left py-2 pr-3">Max Amount (₹)</th>
                <th className="text-left py-2 pr-3">Allowed Roles</th>
                <th className="text-left py-2 pr-3">Allowed Members (optional)</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((t, i) => {
                const isTop = i === matrix.length - 1;
                return (
                  <tr key={i} className="border-b border-slate-100 align-top" data-testid={`matrix-tier-${i}`}>
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-1">
                        <span className="text-slate-400 w-4">{i + 1}</span>
                        <div className="flex flex-col">
                          <button onClick={() => moveMatrixTier(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-[#1F7A6E] disabled:opacity-30" data-testid={`matrix-up-${i}`}><ChevronUp size={12} /></button>
                          <button onClick={() => moveMatrixTier(i, 1)} disabled={i === matrix.length - 1} className="text-slate-400 hover:text-[#1F7A6E] disabled:opacity-30" data-testid={`matrix-down-${i}`}><ChevronDown size={12} /></button>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <input value={t.label || ''} onChange={(e) => updateMatrixTier(i, 'label', e.target.value)} className="w-40 border border-slate-300 rounded-sm px-2 py-1.5 text-sm" data-testid={`matrix-label-${i}`} />
                    </td>
                    <td className="py-2 pr-3">
                      {isTop ? (
                        <span className="text-xs text-slate-500 italic" data-testid={`matrix-max-${i}`}>Unlimited (top level)</span>
                      ) : (
                        <input type="number" value={t.max_amount ?? ''} onChange={(e) => updateMatrixTier(i, 'max_amount', e.target.value === '' ? '' : Number(e.target.value))} placeholder="e.g. 100000" className="w-32 border border-slate-300 rounded-sm px-2 py-1.5 text-sm" data-testid={`matrix-max-${i}`} />
                      )}
                      {!isTop && t.max_amount ? <div className="text-[10px] text-slate-400 mt-0.5">₹{fmtINR(t.max_amount)}</div> : null}
                    </td>
                    <td className="py-2 pr-3">
                      <MultiPicker options={roleOptions} selected={t.allowed_roles} onChange={(v) => updateMatrixTier(i, 'allowed_roles', v)} placeholder="Select roles…" testid={`matrix-roles-${i}`} />
                    </td>
                    <td className="py-2 pr-3">
                      <MultiPicker options={memberOptions} selected={t.allowed_member_ids} onChange={(v) => updateMatrixTier(i, 'allowed_member_ids', v)} placeholder="None" testid={`matrix-members-${i}`} />
                    </td>
                    <td className="py-2">
                      {matrix.length > 1 && !isTop && (
                        <button onClick={() => removeMatrixTier(i)} className="text-rose-500 hover:text-rose-700" data-testid={`matrix-remove-${i}`}><Trash2 size={13} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={saveMatrix} disabled={matrixSaving} className="flex items-center gap-2 px-4 py-2 bg-[#1F7A6E] text-white text-sm rounded-sm font-display font-semibold hover:bg-[#14504E] disabled:opacity-50" data-testid="matrix-save-btn">
            <Save size={14} /> {matrixSaving ? 'Saving…' : 'Save Matrix'}
          </button>
        </div>
      </div>

      {/* ---- Committee Vendor-Selection (vendor choice only — NOT PO sign-off) ---- */}
      <div className="bg-white border border-slate-200 rounded-sm p-5 mt-6" data-testid="committee-config-card">
        <div className="flex items-center gap-2 mb-1">
          <Users size={16} className="text-amber-600" />
          <div className="font-display font-bold text-base">Committee Vendor Selection</div>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          For RFQs whose order value is above the threshold, a committee must record which vendor they chose from the comparative statement before the RFQ can be awarded. This controls the <strong>vendor choice only</strong> — PO approval still follows the approval matrix above.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-display block mb-1">Committee Threshold (₹)</label>
            <input
              type="number"
              value={committeeThreshold}
              onChange={(e) => setCommitteeThreshold(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="100000"
              className="w-48 border border-slate-300 rounded-sm px-2 py-1.5 text-sm"
              data-testid="committee-threshold-input"
            />
            {committeeThreshold ? <div className="text-[10px] text-slate-400 mt-0.5">₹{fmtINR(committeeThreshold)} · orders above this need a committee decision</div> : null}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-display block mb-1">Committee Members (roles &amp; users)</label>
            <MultiPicker options={committeeOptions} selected={committeeMembers} onChange={setCommitteeMembers} placeholder="Select roles / users…" testid="committee-members-picker" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={saveCommittee} disabled={committeeSaving} className="flex items-center gap-2 px-4 py-2 bg-[#1F7A6E] text-white text-sm rounded-sm font-display font-semibold hover:bg-[#14504E] disabled:opacity-50" data-testid="committee-save-btn">
            <Save size={14} /> {committeeSaving ? 'Saving…' : 'Save Committee Config'}
          </button>
        </div>
      </div>

      <div className="mt-6 flex justify-end sticky bottom-4">
        <button data-testid="aw-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 bg-[#1F7A6E] text-white text-sm rounded-sm font-display font-semibold hover:bg-[#14504E] disabled:opacity-50 shadow-lg">
          <Save size={14} /> {saving ? 'Saving…' : 'Save Workflows'}
        </button>
      </div>
    </div>
  );
}
